/**
 * SocialProof — an honest trust band. Rather than fabricated testimonials or
 * borrowed logos, it states verifiable capability facts alongside the core
 * value points, and links to /results where customers' case studies are
 * published with their written consent (R18).
 *
 * The supported-network count is DERIVED LIVE from the publisher capability
 * registry (public GET /api/v1/publishing/public/capabilities-summary) so the
 * landing can never drift from what the product actually publishes to. If the
 * summary is unreachable a non-numeric placeholder is shown — never a number.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useT } from '@/i18n';
import { getPublicCapabilitiesSummary, type PublicCapabilitiesSummary } from '@/lib/api/publicCapabilities';
import {
  PROOF_NETWORK_FALLBACK_VALUE,
  PROOF_SECTION,
} from '@/routes/landing/landing-data';
import { Icon } from './shared';

export function SocialProof() {
  const { t } = useT();
  const [summary, setSummary] = useState<PublicCapabilitiesSummary | null>(null);

  useEffect(() => {
    let alive = true;
    getPublicCapabilitiesSummary()
      .then((s) => {
        if (alive) setSummary(s);
      })
      .catch(() => {
        if (alive) setSummary(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const networkValue = summary ? String(summary.network_count) : PROOF_NETWORK_FALLBACK_VALUE;
  const networkTitle = summary ? summary.networks.map((n) => n.label).join(', ') : undefined;

  return (
    <section id="pourquoi" className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <div className="overflow-hidden rounded-token-xl border border-border bg-surface shadow-elevation-2">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Value points */}
          <div className="p-8 sm:p-10 lg:p-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">{PROOF_SECTION.eyebrow}</p>
            <h2 className="mt-3 text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {PROOF_SECTION.title}
            </h2>
            <ul className="mt-6 space-y-4">
              {PROOF_SECTION.points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                    <Icon name="check" className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="text-pretty leading-relaxed text-foreground">{point}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/results"
              data-testid="proof-results-link"
              className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('adoption.proof.resultsLink')} →
            </Link>
          </div>

          {/* Stat grid on a brand gradient */}
          <div className="relative isolate overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 sm:p-10 lg:p-12">
            <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[radial-gradient(120%_120%_at_15%_0%,rgb(255_255_255/0.18),transparent_55%)]" />
            <div className="grid grid-cols-2 gap-6">
              <div data-testid="proof-network-count" data-live={summary ? 'true' : 'false'} title={networkTitle}>
                <p className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{networkValue}</p>
                <p className="mt-1 text-sm leading-snug text-white/85">{t('adoption.proof.networks')}</p>
              </div>
              {(['languages', 'purposes', 'approval'] as const).map((k) => (
                <div key={k} data-testid={`proof-${k}`}>
                  <p className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{t(`routes.landing.v2.proof.${k}Value`)}</p>
                  <p className="mt-1 text-sm leading-snug text-white/85">{t(`routes.landing.v2.proof.${k}Label`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
